import { formatCurrency, getCurrencySymbol } from '@angular/common';
import { DEFAULT_CURRENCY_CODE, inject, Inject, Injectable, LOCALE_ID, OnDestroy } from '@angular/core';
import { MaterialFilterType } from '@dave/types/dist/proto/erp/material';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CustomerEntity } from '../../../dave-data-module/entities/customer.entity';
import { MaterialEntity } from '../../../dave-data-module/entities/material.entity';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { MaterialDataService } from '../../../dave-data-module/services/material-data.service';
import { PaginatedMaterialService } from '../../../dave-data-module/services/paginated-material.service';
import { State } from '../../../dave-data-module/State';
import { getARLTemplateTypes, getARLTemplateTypesFetched, getQuantityTypeById, getQuantityTypes, getQuantityTypesFetched } from '../../../dave-data-module/State/selectors/accounting.selector';
import { getCustomerDictionary } from '../../../dave-data-module/State/selectors/customers.selectors';
import { getJobSpecification, getJobSpecificationFetched } from '../../../dave-data-module/State/selectors/job-specification.selector';
import { getResourcesActive } from '../../../dave-data-module/State/selectors/resource-dispo/resource.selectors';
import { SelectSearchOption } from '../../../dave-utils-module/select-search/components/select-search/select-search.component';
import { isNotNullOrUndefined } from '../../../helper/helper';
export type arlInformationInputOption = SelectSearchOption<{
    optionLabel: string;
    value: string;
    entityId: number;
}>;
export type MaterialSelectSearchOption = arlInformationInputOption &
    SelectSearchOption<
        {
            deleted: boolean;
            articleNumber?: string;
        },
        arlInformationInputOption & {
            articleNumber?: string;
            supplierId: number;
            cost: number;
            sellPrice: number;
            listPrice: number;
        }
    >;


@Injectable()
export class ArlCalculationDataService implements OnDestroy {
    private readonly _destroy$ = new Subject<void>();

    private _pagedMaterialsService = inject(PaginatedMaterialService);
    private _materialsService = inject(MaterialDataService);
    public quantityTypes$ = getFetched$(this.store, getQuantityTypesFetched, getQuantityTypes).pipe(takeUntil(this._destroy$), shareReplay(1));
    public arlTemplateTypes$ = getFetched$(this.store, getARLTemplateTypesFetched, getARLTemplateTypes).pipe(takeUntil(this._destroy$), shareReplay(1));
    public getQuantityTypeById$(id: number) {
        return getFetched$(this.store, getQuantityTypesFetched, getQuantityTypeById({ id }));
    }
    public getQuantityTypeNameById$(id: number) {
        if (!id) {
            return of('');
        }
        return this.getQuantityTypeById$(id).pipe(map((entity) => entity.Name));
    }
    public resourceOptions$: Observable<SelectSearchOption<{ value: string; entityId: number }>[]> = this.store.select(getResourcesActive).pipe(
        takeUntil(this._destroy$),
        map((resources) => resources.map((r) => ({ optionSearch: [r.Name, r.ArticleNumber, r.DisplayName].filter((v) => v), optionLabel: r.Name, value: r.ARLValues.Information, entityId: r.Id }))),
        shareReplay(1),
    );

    public employeeOptions$: Observable<SelectSearchOption<{ value: string; entityId: number }>[]> = getFetched$(this.store, getJobSpecificationFetched, getJobSpecification).pipe(
        takeUntil(this._destroy$),
        map((jobs) => jobs.map((r) => ({ optionLabel: r.Name, value: r.Name, entityId: r.Id }))),
        shareReplay(1),
    );

    constructor(private store: Store<State>, @Inject(LOCALE_ID) private local: string, @Inject(DEFAULT_CURRENCY_CODE) private currencyCode: string) {}
    public getCurrencyString(num: number): string {
        if (isNotNullOrUndefined(num)) {
            return formatCurrency(num / 100, this.local, getCurrencySymbol(this.currencyCode, 'narrow'), this.currencyCode);
        }
        return num;
    }
    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
    private supplierIdFilterForOptions$ = new BehaviorSubject<number>(null);
    public set supplierIdFilterForOptions(supplierId: number) {
        if (supplierId !== this.supplierIdFilterForOptions$.value) {
            this.supplierIdFilterForOptions$.next(supplierId);
        }
    }
    // private materialIdsFilteredBySupplier$ = combineLatest([
    //     this.supplierIdFilterForOptions$.pipe(
    //         switchMap((supplierId) =>
    //             supplierId
    //                 ? this.materialToSupplierDataService.getMaterialToSupplierBySupplierId$(supplierId).pipe(
    //                       map((m2s) =>
    //                           uniqArray(
    //                               m2s
    //                                   .filter((m) => m.SupplierId === supplierId)
    //                                   .map((m) => m.MaterialId)
    //                                   .filter(isNotNullOrUndefined),
    //                           ),
    //                       ),
    //                   )
    //                 : of(null),
    //         ),
    //     ),
    // ]).pipe(takeUntil(this._destroy$), shareReplay(1));

    // materialOptionsBySupplierId$x = (currentMaterial: number): Observable<{ searchValues: string[]; optionLabel: string; value: string; entityId: number; deleted: boolean }[]> => {
    //     return this.supplierIdFilterForOptions$.pipe(
    //         tap((supplierId) => this._pagedMaterialsService.setFilter([{FilterKey: MaterialFilterType.SupplierFilter, Ids: [supplierId.toString()]}])),
    //         switchMap(supplierId => ),
    //         map(([materials, materialIds]) => {
    //             const notDeletedMaterials = materials.filter((m) => !m.deleted || m.entityId === currentMaterial);
    //             return materialIds ? notDeletedMaterials.filter((m) => materialIds.includes(m.entityId)) : notDeletedMaterials;
    //         }),
    //         shareReplay({ refCount: true, bufferSize: 1 }),
    //     );
    // };
    materialOptionsBySupplierId$: Observable<MaterialSelectSearchOption[]> = this.supplierIdFilterForOptions$.pipe(
        tap(async (supplierId) => {
            const targetFilter = {
                withSupplier: true,//warum?
                withMaterialToSuppliers: true,
                [MaterialFilterType.SupplierFilter]: supplierId ? [supplierId] : undefined,
            }
            const currentFilter = await firstValueFrom(this._pagedMaterialsService.selectFilterObject$);
            if (JSON.stringify(currentFilter) !== JSON.stringify(targetFilter)) {
                this._pagedMaterialsService.setFilter(targetFilter).then(() => {
                    this._pagedMaterialsService.loadPage()
                })
            }
        }),
        switchMap(() => combineLatest([this._pagedMaterialsService.selectItems$, this.store.select(getCustomerDictionary)])),
        map(([v, customerDict]) => v.map((c) => this.materialToSelectOption(c, customerDict))),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );
    materialOption$(id: number): Observable<MaterialSelectSearchOption> {
        return combineLatest([this._materialsService.getMaterialById$(id), this.store.select(getCustomerDictionary)]).pipe(
            map(([m, customerDict]) => {
                if (!m) {
                    console.warn("can't get material by id: " + id);
                }
                return m ? this.materialToSelectOption(m, customerDict) : null;
            }),
        );
    }
    public materialToSelectOption = (m: MaterialEntity, customerDictionary: Dictionary<CustomerEntity>): MaterialSelectSearchOption => {
        const hasM2S = m.MaterialToSuppliers?.length > 0;

        const optionLabel = [m.ArticleNumber, m.Name];

        if (!hasM2S) {
            const supplierId = m.UsedForInformationSupplierIds?.find(() => true) || m.SupplierIds?.find(() => true);
            const supplier = supplierId && customerDictionary && customerDictionary[supplierId];

            optionLabel.unshift(supplier?.DisplayName);
            if (isNotNullOrUndefined(m.ListPrice)) {
                optionLabel.push('LP: ' + this.getCurrencyString(m.ListPrice));
            }
            if (isNotNullOrUndefined(m.Cost)) {
                optionLabel.push('EK: ' + this.getCurrencyString(m.Cost));
            }
            if (isNotNullOrUndefined(m.SellPrice)) {
                optionLabel.push('VK: ' + this.getCurrencyString(m.SellPrice));
            }
        }
        return {
            optionLabel: optionLabel.filter((v) => !!v).join(', '),
            value: m.ARLValues.Information,
            articleNumber: m.ArticleNumber,
            entityId: m.Id,
            deleted: !!m.DeletedAt,
            childOptions: hasM2S
                ? m.MaterialToSuppliers.map((m2s) => {
                      const optionLabel = [customerDictionary && customerDictionary[+m2s.SupplierId]?.DisplayName];
                      if (isNotNullOrUndefined(m2s.ListPrice)) {
                          optionLabel.push('LP: ' + this.getCurrencyString(Number(m2s.ListPrice)));
                      }
                      if (isNotNullOrUndefined(m2s.Cost)) {
                          optionLabel.push('EK: ' + this.getCurrencyString(Number(m2s.Cost)));
                      }
                      if (isNotNullOrUndefined(m2s.SellPrice)) {
                          optionLabel.push('VK: ' + this.getCurrencyString(Number(m2s.SellPrice)));
                      }
                      return {
                          entityId: m.Id,
                          supplierId: +m2s.SupplierId,
                          value: m.ARLValues.Information,
                          articleNumber: m2s.ArticleNumber || m.ArticleNumber,
                          listPrice: Number(m2s.ListPrice),
                          cost: Number(m2s.Cost),
                          sellPrice: Number(m2s.SellPrice),
                          optionLabel: optionLabel.filter((v) => !!v).join(', '),
                      };
                  })
                : null,
        };
    };
}
